/**
 * Where a dragged card lands, and who it pushes out of the way.
 *
 * Three rules, in order:
 *
 *   1. SNAP to the lattice the rest of the graph already uses — the distinct column x's
 *      and row y's of the other cards. Not an arbitrary 16px grid: aligning to the
 *      layout's own columns is what makes a hand-placed card look deliberate, and it
 *      keeps manual and derived positions readable as one drawing.
 *   2. If that slot is TAKEN, the occupant is PUSHED one slot away — along whichever
 *      axis the drop was more off-centre on, in the direction that gets it clear.
 *      Dropping slightly above a card sends it down a row; slightly left sends it right.
 *   3. A pushed card that lands on someone else pushes THEM, and so on.
 *
 * ## The cost of rule 2, stated plainly
 *
 * A card's position is normally DERIVED — only hand-placed ones live in `positions`.
 * Pushing a derived card therefore pins it, and a cascade pins everything it touches.
 * A few drags can turn a laid-out graph into a hand-placed one, with 「重排」 the only
 * way back. That is the deal pushing buys: it is a diagram editor's behaviour, and this
 * becomes a diagram editor the first time it is used.
 *
 * ## What this deliberately does NOT do
 *
 * Keep cards off the connectors. A card covering a line is a real annoyance, but edges
 * are computed FROM positions: moving a card changes the edges, which changes where it
 * should move to. That feedback loop makes for jumpy, unpredictable drops. Routing
 * around obstacles belongs in the edge router, not the drop handler.
 *
 * Pure and synchronous, so it is testable and cannot surprise the reducer.
 */

export interface Rect {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlanDropInput {
  /** The card being dropped. */
  id: string;
  /** Where the pointer left it. */
  at: { x: number; y: number };
  /** Every card currently on the canvas, including the dragged one. */
  rects: Rect[];
  /** Column pitch, used to extend the lattice past the existing cards. */
  colPitch: number;
  /** Row pitch, same purpose. */
  rowPitch: number;
}

export interface DropPlan {
  /** Where the dragged card goes. */
  at: { x: number; y: number };
  /**
   * Cards displaced to make room, in cascade order. Empty for a drop into free space,
   * which is the common case.
   */
  pushed: { id: string; x: number; y: number }[];
}

/** Do two rects overlap? Touching edges do not count. */
function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/**
 * Candidate coordinates along one axis: every value the other cards use, plus a few
 * steps beyond each end.
 *
 * The extension matters — without it a card dragged to empty space on the right would
 * snap back to the rightmost existing column, which feels like the canvas fighting you.
 */
function candidates(values: number[], pitch: number, want: number): number[] {
  if (values.length === 0) return [want];
  const set = new Set(values);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const beyond = Math.min(24, Math.ceil(Math.abs(want - (want > max ? max : min)) / pitch) + 2);
  for (let k = 1; k <= beyond; k++) {
    set.add(max + k * pitch);
    set.add(min - k * pitch);
  }
  return [...set].sort((a, b) => a - b);
}

function nearest(values: number[], want: number): number {
  return values.reduce(
    (best, v) => (Math.abs(v - want) < Math.abs(best - want) ? v : best),
    values[0],
  );
}

/** The next lattice value after `from` in direction `step` (+1 / -1). */
function step(values: number[], from: number, dir: 1 | -1, pitch: number): number {
  const sorted = dir === 1 ? values : [...values].reverse();
  const next = sorted.find((v) => (dir === 1 ? v > from + 0.5 : v < from - 0.5));
  // Past the end of the lattice, keep going at the pitch rather than refusing to move.
  return next ?? from + dir * pitch;
}

/** How deep a cascade may go before we stop pushing. */
const MAX_CASCADE = 12;

/**
 * Resolve a drop: snap it, then push whoever is in the way.
 *
 * Returns the raw position unchanged when there is nothing to align to (a single-card
 * graph), because snapping to yourself is meaningless.
 */
export function planDrop(input: PlanDropInput): DropPlan {
  const { id, at, rects, colPitch, rowPitch } = input;
  const self = rects.find((r) => r.id === id);
  if (!self) return { at, pushed: [] };
  const others = rects.filter((r) => r.id !== id);
  if (others.length === 0) return { at, pushed: [] };

  const xs = candidates([...new Set(others.map((r) => r.x))], colPitch, at.x);
  const ys = candidates([...new Set(others.map((r) => r.y))], rowPitch, at.y);
  const snapped = { x: nearest(xs, at.x), y: nearest(ys, at.y) };

  // Live positions, mutated as the cascade proceeds.
  const live = new Map<string, Rect>(others.map((r) => [r.id, { ...r }]));
  const placed: Rect = { ...self, x: snapped.x, y: snapped.y };

  /*
   * Push direction, decided ONCE from the drop itself and then reused for the whole
   * cascade. Taken from which axis the pointer was more off-centre on, relative to the
   * card being displaced: drop above its middle and it goes down, drop left of its
   * middle and it goes right. Keeping one direction for the cascade is what makes the
   * result predictable — a per-card direction would fan cards out unpredictably.
   */
  const first = [...live.values()].find((o) => overlaps(placed, o));
  if (!first) return { at: snapped, pushed: [] };

  const dx = at.x + self.w / 2 - (first.x + first.w / 2);
  const dy = at.y + self.h / 2 - (first.y + first.h / 2);
  const vertical = Math.abs(dy) >= Math.abs(dx);
  // Away from the incoming card: if we came in above it (dy < 0) it moves down.
  const dir: 1 | -1 = (vertical ? dy <= 0 : dx <= 0) ? 1 : -1;

  const pushed: { id: string; x: number; y: number }[] = [];
  const queue: Rect[] = [placed];
  let guard = 0;

  while (queue.length > 0 && guard++ < MAX_CASCADE) {
    const mover = queue.shift()!;
    for (const victim of [...live.values()]) {
      if (victim.id === mover.id || !overlaps(mover, victim)) continue;
      const moved: Rect = vertical
        ? { ...victim, y: step(ys, victim.y, dir, rowPitch) }
        : { ...victim, x: step(xs, victim.x, dir, colPitch) };
      live.set(victim.id, moved);
      // Replace any earlier entry for this card: a cascade can touch one twice, and
      // only its final resting place should be reported.
      const prev = pushed.findIndex((p) => p.id === victim.id);
      if (prev >= 0) pushed.splice(prev, 1);
      pushed.push({ id: moved.id, x: moved.x, y: moved.y });
      queue.push(moved);
    }
  }

  return { at: snapped, pushed };
}
